import { useState, useMemo } from 'react';

import {
  Button,
  Chip,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import scssVariables from '@styles/variables.module.scss';

import { YearFilterProps } from './types';

const YearFilter = ({
  value,
  onChange,
  years = [],
  fiscalYearStartMonth = 1,
  maxVisibleYears = 5,
  variant = 'chips',
  size = 'medium',
  orientation = 'horizontal',
  showAllYears = false,
  customStyles,
  'data-automation-id': dataAutomationId,
}: YearFilterProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Generate default years if none provided
  const defaultYears = useMemo(() => {
    if (years.length > 0) return years;

    const currentYear = new Date().getFullYear();
    return Array.from({ length: 10 }, (_, i) => currentYear - i);
  }, [years]);

  const visibleYears = defaultYears.slice(0, maxVisibleYears);
  const hiddenYears = defaultYears.slice(maxVisibleYears);

  const formatYearLabel = (year: number) => {
    if (!fiscalYearStartMonth || fiscalYearStartMonth === 1) {
      return `${year}`;
    }

    const endYear = year + 1;
    return `${year} / ${endYear}`;
  };

  const handleYearChange = (event: SelectChangeEvent) => {
    onChange(Number(event.target.value));
  };

  const handleChipClick = (year: number) => {
    onChange(year);
  };

  const handleButtonClick = (year: number) => {
    onChange(year);
  };

  const handleMoreClick = (event: { currentTarget: HTMLElement }) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuYearSelect = (year: number) => {
    onChange(year);
    setAnchorEl(null);
  };

  // Select variant
  if (variant === 'select') {
    return (
      <Select
        value={value.toString()}
        size={size}
        label="Year"
        onChange={handleYearChange}
        autoWidth
        sx={customStyles?.select}
        data-automation-id={dataAutomationId}
      >
        {defaultYears.map((year) => (
          <MenuItem key={year} value={year}>
            {formatYearLabel(year)}
          </MenuItem>
        ))}
      </Select>
    );
  }

  // Buttons variant
  if (variant === 'buttons') {
    return (
      <Stack
        direction={orientation === 'vertical' ? 'column' : 'row'}
        spacing={1}
        sx={customStyles?.container}
        data-automation-id={dataAutomationId}
      >
        {defaultYears.map((year) => (
          <Button
            key={year}
            variant={value === year ? 'contained' : 'outlined'}
            size={size}
            onClick={() => handleButtonClick(year)}
            sx={{
              borderRadius: 5, // Increased border radius for more rounded appearance
              ...customStyles?.chip,
              ...(value === year && {
                bgcolor: scssVariables.ColorBlue10,
                color: scssVariables.ColorBlue80,
                borderColor: scssVariables.ColorBlue80,
                '&:hover': {
                  bgcolor: scssVariables.ColorBlue10,
                  opacity: 0.9,
                },
                ...customStyles?.selectedChip,
              }),
              ...(value !== year && {
                color: scssVariables.ColorBlack60,
                borderColor: scssVariables.ColorBlack60,
                '&:hover': {
                  bgcolor: 'rgba(104, 111, 120, 0.04)',
                },
              }),
            }}
            data-automation-id={`${dataAutomationId}-button-${year}`}
          >
            {formatYearLabel(year)}
          </Button>
        ))}
      </Stack>
    );
  }

  // Chips variant (default)
  return (
    <Stack
      direction={orientation === 'vertical' ? 'column' : 'row'}
      spacing={1}
      sx={customStyles?.container}
      data-automation-id={dataAutomationId}
    >
      {visibleYears.map((year) => (
        <Chip
          key={year}
          label={formatYearLabel(year)}
          onClick={() => handleChipClick(year)}
          variant="outlined"
          size={size}
          sx={{
            borderRadius: 6, // Increased border radius for more rounded appearance
            ...customStyles?.chip,
            ...(value === year && {
              bgcolor: scssVariables.ColorBlue10,
              color: scssVariables.ColorBlue80,
              borderColor: scssVariables.ColorBlue80,
              '&:hover': {
                bgcolor: scssVariables.ColorBlue10,
                opacity: 0.9,
              },
              ...customStyles?.selectedChip,
            }),
            ...(value !== year && {
              color: scssVariables.ColorBlack60,
              borderColor: scssVariables.ColorBlack60,
              '&:hover': {
                bgcolor: 'rgba(104, 111, 120, 0.04)',
              },
            }),
          }}
          data-automation-id={`${dataAutomationId}-chip-${year}`}
        />
      ))}

      {/* Show "..." chip if there are hidden years or if showAllYears is true */}
      {(hiddenYears.length > 0 || showAllYears) && (
        <>
          <Chip
            label="…"
            variant="outlined"
            onClick={handleMoreClick}
            size={size}
            sx={{
              borderRadius: 6, // Increased border radius for more rounded appearance
              color: scssVariables.ColorBlack60,
              borderColor: scssVariables.ColorBlack60,
              ...customStyles?.chip,
            }}
            data-automation-id={`${dataAutomationId}-more-chip`}
          />
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            data-automation-id={`${dataAutomationId}-menu`}
          >
            {hiddenYears.map((year) => (
              <MenuItem
                key={year}
                selected={year === value}
                onClick={() => handleMenuYearSelect(year)}
                data-automation-id={`${dataAutomationId}-menu-item-${year}`}
              >
                {formatYearLabel(year)}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Stack>
  );
};

export default YearFilter;
